import React from 'react';
import { useTranslation } from 'react-i18next';
import './AgentStatusBreakdownWidget.css';

/**
 * AgentStatusBreakdownWidget — répartition du temps par statut, par agent (KPI
 * agent-status-statistics). Une barre empilée 100% par agent (segments = statuts),
 * lignes groupées par queue. Statuts dynamiques : couleurs assignées de façon stable
 * (READY en vert, le reste depuis une palette, ordre déterministe → cohérent entre agents).
 */
// Palette catégorielle ancrée sur le thème Heedify (navy / teal / gold + dérivées harmonieuses).
const PALETTE = [
    '#1B3A6B', '#00A4A6', '#FFC700', '#6C4FA1', '#22497F', '#008E90',
    '#FF8C00', '#4A7BB0', '#3FA796', '#B5546F', '#8C6D1F', '#5A6BA8'
];
const READY_COLOR = '#107C10'; // = --heedify-success

// Statuts SYSTÈME → clés i18n. Les reason codes (Coffee break, Lunch…) sont des libellés
// configurés par l'admin : on les laisse passer via t() avec defaultValue = le libellé brut
// (traduits si une clé existe, sinon affichés tels quels). Séparateurs i18next désactivés pour
// ne pas casser sur un libellé contenant « . » ou « : ».
const SYSTEM_STATUS_KEY = { READY: 'STATUS_READY', UNAVAILABLE: 'STATUS_UNAVAILABLE', OFFLINE: 'STATUS_OFFLINE' };

const AgentStatusBreakdownWidget = ({ data, config }) => {
    const { t } = useTranslation();

    if (!Array.isArray(data) || data.length === 0) {
        return null;
    }

    const tStatus = (s) => {
        const key = SYSTEM_STATUS_KEY[String(s).toUpperCase()];
        return key ? t(key) : t(s, { defaultValue: s, nsSeparator: false, keySeparator: false });
    };

    const fmtPct = (n) => {
        const v = Number(n);
        return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}%`;
    };

    const fmtDur = (s) => {
        if (s === null || s === undefined) return '-';
        const total = Math.max(0, Math.round(Number(s)));
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const sec = total % 60;
        const pad = (x) => String(x).padStart(2, '0');
        return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
    };

    const isReady = (s) => String(s).toUpperCase() === 'READY';

    // Union des statuts (ordre stable : READY d'abord, puis alpha) + map de couleurs déterministe.
    const stateSet = new Set();
    data.forEach((r) => Object.keys(r.statuses || {}).forEach((s) => stateSet.add(s)));
    const states = Array.from(stateSet).sort((a, b) => {
        if (isReady(a)) return -1;
        if (isReady(b)) return 1;
        return a.localeCompare(b);
    });
    const colorOf = {};
    let pi = 0;
    states.forEach((s) => {
        colorOf[s] = isReady(s) ? READY_COLOR : PALETTE[pi++ % PALETTE.length];
    });

    // Regroupement par queue (donnée déjà triée queue puis agent).
    const groups = [];
    const idx = {};
    data.forEach((r) => {
        const k = r.queueName || '—';
        if (idx[k] === undefined) {
            idx[k] = groups.length;
            groups.push({ queueName: k, rows: [] });
        }
        groups[idx[k]].rows.push(r);
    });

    const segmentsFor = (r) => {
        const entries = Object.entries(r.statuses || {}).filter(([, v]) => Number(v) > 0);
        const total = entries.reduce((acc, [, v]) => acc + Number(v), 0) || 1;
        entries.sort((a, b) => states.indexOf(a[0]) - states.indexOf(b[0]));
        return entries.map(([state, sec]) => ({
            state,
            sec: Number(sec),
            pct: (Number(sec) / total) * 100
        }));
    };

    return (
        <div className="asb-widget">
            <div className="asb-legend">
                {states.map((s) => (
                    <span className="asb-legend-item" key={s}>
                        <span className="asb-swatch" style={{ background: colorOf[s] }} />
                        {tStatus(s)}
                    </span>
                ))}
            </div>
            <div className="asb-scroll">
                {groups.map((g, gi) => (
                    <div className="asb-group" key={gi}>
                        <div className="asb-queue">{g.queueName}</div>
                        {g.rows.map((r, ri) => {
                            const segs = segmentsFor(r);
                            return (
                                <div className="asb-row" key={ri}>
                                    <div className="asb-agent" title={r.agentName}>{r.agentName}</div>
                                    <div className="asb-bar">
                                        {segs.map((seg, si) => (
                                            <div
                                                key={si}
                                                className="asb-seg"
                                                style={{ width: `${seg.pct}%`, background: colorOf[seg.state] }}
                                                title={`${tStatus(seg.state)}: ${fmtDur(seg.sec)} (${fmtPct(seg.pct)})`}
                                            >
                                                {seg.pct >= 10 ? (
                                                    <span className="asb-seg-label">{fmtPct(seg.pct)}</span>
                                                ) : null}
                                            </div>
                                        ))}
                                    </div>
                                    {/* Total de la barre = somme de SES segments, et non
                                        `totalLoggedOn` : cette colonne du backend porte le temps
                                        CONNECTÉ, c'est-à-dire le temps Ready seul. L'afficher ici
                                        donnerait un nombre qui ne correspond pas au dessin, dont
                                        les segments couvrent tous les statuts. */}
                                    <div className="asb-total">{fmtDur(segs.reduce((acc, s) => acc + s.sec, 0))}</div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AgentStatusBreakdownWidget;
